/**
 * AudioYMixer
 * -----------
 * Y-splitter audio WebRTC → 2 sorties simultanées :
 *   - Branche 1 : speakers (AudioContext.destination)
 *   - Branche 2 : PCM capture → Gladia (via ScriptProcessorNode)
 *
 * Résout le problème Chrome : remote WebRTC track muted=true en permanence
 * malgré un audio audible sur <video>.
 *
 * IMPORTANT: AudioContext uses browser default sampleRate (usually 48kHz).
 * The ScriptProcessor downsamples to targetSampleRate (16kHz) for Gladia.
 * Forcing sampleRate=16000 on the AudioContext causes createMediaElementSource
 * to produce silence on some Chrome versions.
 */

export interface AudioYMixerOptions {
  /**
   * Target sample rate for the PCM output sent to Gladia.
   * Downsampling from native rate is done in the ScriptProcessor.
   * Default: 16000
   */
  targetSampleRate?: number;

  /**
   * Activer/désactiver la branche speakers.
   * Default: true
   */
  enableSpeakers?: boolean;

  /**
   * Taille FFT pour les AnalyserNodes (debug waveform).
   * Default: 2048
   */
  fftSize?: number;

  /**
   * Buffer size for ScriptProcessorNode (PCM capture).
   * Default: 4096
   */
  pcmBufferSize?: number;
}

/** Callback receiving raw PCM Int16 LE chunks for Gladia */
export type PCMCallback = (pcmData: ArrayBuffer) => void;

import { createLogger } from "./logger";
const slog = createLogger("audio-mixer");

export class AudioYMixer {
  private ctx: AudioContext;
  private source: MediaStreamAudioSourceNode | MediaElementAudioSourceNode | null = null;
  private scriptNode: ScriptProcessorNode | null = null;
  private speakerGain: GainNode | null = null;
  private readonly enableSpeakers: boolean;
  private readonly fftSize: number;
  private readonly pcmBufferSize: number;
  private readonly targetSampleRate: number;
  private pcmCallback: PCMCallback | null = null;

  /** AnalyserNode on the input (WebRTC output, before Y-split) */
  analyserInput: AnalyserNode | null = null;
  /** AnalyserNode on the speakers branch */
  analyserSpeakers: AnalyserNode | null = null;
  /** AnalyserNode on the Gladia capture branch */
  analyserGladia: AnalyserNode | null = null;

  constructor(options: AudioYMixerOptions = {}) {
    const {
      targetSampleRate = 16000,
      enableSpeakers = true,
      fftSize = 2048,
      pcmBufferSize = 4096,
    } = options;
    // Use browser default sampleRate (usually 48000) — do NOT force 16kHz
    this.ctx = new AudioContext();
    this.enableSpeakers = enableSpeakers;
    this.fftSize = fftSize;
    this.pcmBufferSize = pcmBufferSize;
    this.targetSampleRate = targetSampleRate;
  }

  /** Actual AudioContext sample rate (browser native, usually 48000) */
  get nativeSampleRate(): number {
    return this.ctx.sampleRate;
  }

  connectTrack(track: MediaStreamTrack): void {
    if (track.muted) {
      slog.warn("connectTrack() sur track muted. Préférer connectElement().");
    }
    if (track.readyState === 'ended') {
      throw new Error('[AudioYMixer] Track readyState=ended.');
    }
    const inputStream = new MediaStream([track]);
    this.source = this.ctx.createMediaStreamSource(inputStream);
    this._wire();
  }

  connectElement(el: HTMLVideoElement | HTMLAudioElement): void {
    // Try captureStream() first — more reliable than createMediaElementSource
    // for WebRTC streams in Chrome. captureStream() captures rendered audio
    // including the internal WebRTC audio pipeline.
    try {
      const captured = (el as unknown as { captureStream(): MediaStream }).captureStream();
      const audioTrack = captured.getAudioTracks()[0];
      if (audioTrack && audioTrack.readyState === 'live') {
        slog.info("Using captureStream()", { trackId: audioTrack.id });
        this.source = this.ctx.createMediaStreamSource(new MediaStream([audioTrack]));
        this._wire();
        return;
      }
    } catch (e) {
      slog.warn("captureStream() failed, falling back to createMediaElementSource", { error: String(e) });
    }

    // Fallback: createMediaElementSource (may produce silence on WebRTC in some Chrome versions)
    slog.info("Using createMediaElementSource fallback");
    this.source = this.ctx.createMediaElementSource(el);
    this._wire();
  }

  onPCM(callback: PCMCallback | null): void {
    this.pcmCallback = callback;
  }

  // ---------------------------------------------------------------------------
  // Graph:
  //  source → [analyserInput] ─┬→ [analyserSpeakers] → ctx.destination
  //                             └→ [analyserGladia] → [ScriptProcessor] → (PCM↓16k)
  // ---------------------------------------------------------------------------
  private _wire(): void {
    if (!this.source) throw new Error('[AudioYMixer] Source non initialisée.');

    // Input analyser
    this.analyserInput = this.ctx.createAnalyser();
    this.analyserInput.fftSize = this.fftSize;
    this.source.connect(this.analyserInput);

    if (this.enableSpeakers) {
      this.analyserSpeakers = this.ctx.createAnalyser();
      this.analyserSpeakers.fftSize = this.fftSize;
      this.speakerGain = this.ctx.createGain();
      this.analyserInput.connect(this.analyserSpeakers);
      this.analyserSpeakers.connect(this.speakerGain);
      this.speakerGain.connect(this.ctx.destination);
    }

    // Gladia branch with PCM capture
    this.analyserGladia = this.ctx.createAnalyser();
    this.analyserGladia.fftSize = this.fftSize;
    this.analyserInput.connect(this.analyserGladia);

    this.scriptNode = this.ctx.createScriptProcessor(this.pcmBufferSize, 1, 1);

    const nativeRate = this.ctx.sampleRate;
    const targetRate = this.targetSampleRate;
    const ratio = nativeRate / targetRate; // e.g. 48000/16000 = 3

    let debugCounter = 0;
    this.scriptNode.onaudioprocess = (e) => {
      const input = e.inputBuffer.getChannelData(0);

      // Debug: log peak every ~1 second (at 48kHz with 4096 buffer → ~12 calls/sec)
      debugCounter++;
      if (debugCounter % 12 === 1) {
        let peak = 0;
        for (let i = 0; i < input.length; i++) {
          const abs = Math.abs(input[i]!);
          if (abs > peak) peak = abs;
        }
        const db = peak > 0 ? (20 * Math.log10(peak)).toFixed(1) : '-inf';
        slog.debug("ScriptProcessor tick", { peakDb: db, samples: input.length, hasCallback: !!this.pcmCallback });
      }

      if (!this.pcmCallback) return;

      // Downsample from native rate to target rate (simple decimation)
      const outLen = Math.floor(input.length / ratio);
      const pcm16 = new Int16Array(outLen);
      for (let i = 0; i < outLen; i++) {
        const s = Math.max(-1, Math.min(1, input[Math.floor(i * ratio)]!));
        pcm16[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
      }
      this.pcmCallback(pcm16.buffer);
    };

    this.analyserGladia.connect(this.scriptNode);
    // ScriptProcessor MUST connect to destination to fire events
    const silentGain = this.ctx.createGain();
    silentGain.gain.value = 0;
    this.scriptNode.connect(silentGain);
    silentGain.connect(this.ctx.destination);
  }

  setSpeakersMuted(muted: boolean): void {
    if (this.speakerGain) {
      this.speakerGain.gain.value = muted ? 0 : 1;
    }
  }

  /** Set speaker output volume (0.0 = silent, 1.0 = full). Gladia branch unaffected. */
  setSpeakerVolume(vol: number): void {
    if (this.speakerGain) {
      this.speakerGain.gain.value = Math.max(0, Math.min(1, vol));
    }
  }

  async resume(): Promise<void> {
    if (this.ctx.state === 'suspended') {
      await this.ctx.resume();
    }
  }

  get state(): AudioContextState {
    return this.ctx.state;
  }

  destroy(): void {
    this.pcmCallback = null;
    if (this.scriptNode) {
      this.scriptNode.onaudioprocess = null;
      this.scriptNode.disconnect();
    }
    this.source?.disconnect();
    this.analyserInput?.disconnect();
    this.analyserSpeakers?.disconnect();
    this.analyserGladia?.disconnect();
    this.speakerGain?.disconnect();
    this.ctx.close();
    this.source = null;
    this.scriptNode = null;
    this.analyserInput = null;
    this.analyserSpeakers = null;
    this.analyserGladia = null;
    this.speakerGain = null;
  }
}
